import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Package, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type BundleItem = { productId: number; productName: string; quantity: number; stock: number; unit: string };
type Bundle = {
  id: number;
  name: string;
  sku: string;
  description: string | null;
  sellingPrice: number;
  isActive: boolean;
  items: BundleItem[];
  createdAt: string;
};
type Product = { id: number; name: string; sku: string; price: number; stock: number; unit: string };

const EMPTY_BUNDLE = { name: "", sku: "", description: "", sellingPrice: 0 };

function generateBundleSKU() {
  return "BDL-" + Math.random().toString(36).toUpperCase().slice(2, 8);
}

async function readArrayResponse<T>(response: Response, label: string, envelopeKey: string): Promise<T[]> {
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload
      ? String((payload as { error: unknown }).error)
      : `Could not load ${label}`;
    throw new Error(message);
  }
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object") {
    const enveloped = (payload as Record<string, unknown>)[envelopeKey];
    if (Array.isArray(enveloped)) return enveloped as T[];
  }
  throw new Error(`Unexpected ${label} response`);
}

export default function ProductBundles() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: bundles = [], isLoading, error: bundlesError } = useQuery<Bundle[]>({
    queryKey: ["product-bundles"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/product-bundles`, { credentials: "include" });
      return readArrayResponse<Bundle>(r, "product bundles", "bundles");
    },
  });

  const { data: products = [], error: productsError } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/products`, { credentials: "include" });
      return readArrayResponse<Product>(r, "products", "products");
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Bundle> & typeof EMPTY_BUNDLE>(EMPTY_BUNDLE);
  const [isEdit, setIsEdit] = useState(false);
  const [bundleItems, setBundleItems] = useState<BundleItem[]>([]);
  const [productSearch, setProductSearch] = useState("");

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 8);

  const addItem = (p: Product) => {
    setBundleItems(prev => {
      const exists = prev.find(i => i.productId === p.id);
      if (exists) return prev.map(i => i.productId === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId: p.id, productName: p.name, quantity: 1, stock: p.stock, unit: p.unit }];
    });
    setProductSearch("");
  };

  const removeItem = (productId: number) => setBundleItems(prev => prev.filter(i => i.productId !== productId));
  const updateQty = (productId: number, qty: number) => setBundleItems(prev => prev.map(i => i.productId === productId ? { ...i, quantity: Math.max(1, qty) } : i));

  const reset = () => { setEditing(EMPTY_BUNDLE); setBundleItems([]); setProductSearch(""); setIsEdit(false); };
  const openNew = () => { reset(); setIsOpen(true); };
  const openEdit = (b: Bundle) => {
    setEditing({ ...b, description: b.description ?? "" });
    setBundleItems(b.items);
    setIsEdit(true);
    setIsOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const url = isEdit ? `${BASE}/api/product-bundles/${(editing as any).id}` : `${BASE}/api/product-bundles`;
      const r = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editing.name,
          sku: editing.sku,
          description: editing.description || null,
          sellingPrice: Number(editing.sellingPrice),
          items: bundleItems.map(i => ({ productId: i.productId, quantity: i.quantity })),
        }),
      });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-bundles"] });
      setIsOpen(false);
      reset();
      toast({ title: isEdit ? "Bundle updated" : "Bundle created" });
    },
    onError: () => toast({ title: "Failed to save bundle", variant: "destructive" }),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const res = await fetch(`${BASE}/api/product-bundles/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) throw new Error("Failed to update bundle");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-bundles"] }),
    onError: () => toast({ title: "Failed to update bundle", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/product-bundles/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete bundle");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-bundles"] }),
    onError: () => toast({ title: "Failed to delete bundle", variant: "destructive" }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Product Bundles</h1>
          <p className="text-muted-foreground mt-1">Combine products into a bundle and sell as a single item at a package price. Each component's stock is deducted when sold.</p>
        </div>
        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button onClick={openNew}><Plus className="w-4 h-4 mr-2" /> New Bundle</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEdit ? "Edit Bundle" : "Create Product Bundle"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bundle name <span className="text-destructive">*</span></label>
                  <Input className="mt-1" placeholder="e.g. Breakfast Combo" value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium">SKU / Code</label>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="BDL-XXXXX" value={editing.sku} onChange={e => setEditing(p => ({ ...p, sku: e.target.value }))} />
                    <Button type="button" variant="outline" className="shrink-0 text-xs" onClick={() => setEditing(p => ({ ...p, sku: generateBundleSKU() }))}>Gen</Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bundle selling price (₦) <span className="text-destructive">*</span></label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₦</span>
                    <Input type="number" min={0} step="0.01" className="pl-7" value={editing.sellingPrice || ""} onChange={e => setEditing(p => ({ ...p, sellingPrice: Number(e.target.value) }))} placeholder="0.00" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Input className="mt-1" placeholder="Brief description" value={editing.description ?? ""} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Add products to bundle</label>
                <Input
                  placeholder="Search product by name or SKU..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                />
                {productsError && (
                  <p className="mt-2 text-xs text-destructive">Products could not be loaded: {productsError.message}</p>
                )}
                {productSearch && filteredProducts.length > 0 && (
                  <div className="border rounded-lg mt-1 divide-y max-h-48 overflow-y-auto">
                    {filteredProducts.map(p => (
                      <button key={p.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between" onClick={() => addItem(p)}>
                        <span>{p.name}</span>
                        <span className="text-muted-foreground">{p.sku} · stock {p.stock} {p.unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {bundleItems.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="w-24">Qty in bundle</TableHead>
                        <TableHead className="w-28">Available stock</TableHead>
                        <TableHead className="w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bundleItems.map(item => (
                        <TableRow key={item.productId}>
                          <TableCell className="font-medium">{item.productName}</TableCell>
                          <TableCell><Input type="number" min={1} value={item.quantity} onChange={e => updateQty(item.productId, Number(e.target.value))} className="h-8 w-20" /></TableCell>
                          <TableCell>
                            <span className={item.stock < item.quantity ? "text-destructive font-medium" : "text-muted-foreground text-sm"}>
                              {item.stock} {item.unit}
                              {item.stock < item.quantity && " ⚠"}
                            </span>
                          </TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(item.productId)}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="px-4 py-2 bg-muted/30 border-t text-xs text-muted-foreground">
                    Max bundles you can sell based on current stock: <strong>{bundleItems.length > 0 ? Math.floor(Math.min(...bundleItems.map(i => i.stock / i.quantity))) : 0}</strong>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg py-8 text-center text-muted-foreground text-sm">
                  Search and add products above to build your bundle
                </div>
              )}

              <Button
                className="w-full"
                disabled={save.isPending || !editing.name || !editing.sku || !editing.sellingPrice || bundleItems.length === 0}
                onClick={() => save.mutate()}
              >
                {save.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {isEdit ? "Save changes" : "Create bundle"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bundle</TableHead>
              <TableHead>Contents</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Max sellable</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : bundlesError ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-destructive">
                  <p className="font-medium">Product bundles could not be loaded.</p>
                  <p className="mt-1 text-xs">{bundlesError.message}</p>
                </TableCell>
              </TableRow>
            ) : bundles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-14 text-muted-foreground">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No bundles yet.</p>
                  <p className="text-xs mt-1">Create combos like "Breakfast Pack" or "Back-to-School Set" to sell multiple items as one product.</p>
                </TableCell>
              </TableRow>
            ) : bundles.map(b => (
              <TableRow key={b.id} className={!b.isActive ? "opacity-50" : ""}>
                <TableCell>
                  <div>
                    <p className="font-medium">{b.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{b.sku}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {b.items.slice(0, 3).map(i => (
                      <Badge key={i.productId} variant="secondary" className="text-xs">
                        {i.quantity}× {i.productName}
                      </Badge>
                    ))}
                    {b.items.length > 3 && <Badge variant="outline" className="text-xs">+{b.items.length - 3} more</Badge>}
                  </div>
                </TableCell>
                <TableCell className="font-semibold">{formatCurrency(b.sellingPrice)}</TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {b.items.length > 0 ? Math.floor(Math.min(...b.items.map(i => i.stock / i.quantity))) : 0} bundles
                  </span>
                </TableCell>
                <TableCell>
                  <Switch checked={b.isActive} onCheckedChange={v => toggleActive.mutate({ id: b.id, isActive: v })} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(b)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { if (confirm("Delete this bundle?")) remove.mutate(b.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {bundles.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800 flex items-start gap-2">
          <Tag className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Bundles appear at the New Sale POS. When a bundle is sold, each component's stock is automatically deducted.</span>
        </div>
      )}
    </div>
  );
}
